<script>
  import _ from 'underscore'
  import uuidv4 from 'uuid/v4'
  import ItemBadge from './ItemBadge.vue'

  /**
   * @typedef {Object} ItemInfo - アイテム情報
   * @property {string} ItemInfo.collection - コレクション名
   * @property {string} ItemInfo.id         - アイテム ID
   * @property {string} ItemInfo.rare       - レアリティ [ WR | ER | JR | RR | KR | IR | FR | HR | PR | SR | R | N ]
   * @property {string} ItemInfo.name       - 名称
   * @property {string} ItemInfo.imgUrl     - サムネール画像 URL
   * @property {string} ItemInfo.kind       - 種別     [ ワンピース | トップス | ボトムス | シューズ | ヘアアクセ | パシャッとアイテム ]
   * @property {string} ItemInfo.color      - カラー   [ 桃 | 黄 | 青 | 金 | 黒 | 赤 | 緑 | 紫 | 白 | 虹 | 銀 | 水 | 橙 | 茶 ]
   * @property {string} ItemInfo.brand      - ブランド [ SH | GY | SA | CH | DS | DW | RB | UQ | PM | MR | LP | FT | MM ]
   * @property {string} ItemInfo.type       - 属性     [ LO | PO | CO | SE | NT | PR ]
   * @property {string} ItemInfo.point      - いいね数
   *
   *
   * @property {string} ItemInfo.aggregation - 所持ステータス集約先アイテム（トップスやワンピース）の ID。
   *
   * @property {Object}  ItemInfo.numOwned          - null または { usual: number, nico: number, kira: number }
   * @property {number}  ItemInfo.numOwned.usual    - 自排入手の所持枚数（ノーマルプリチケ型）
   * @property {number}  ItemInfo.numOwned.nico     - 自排入手の所持枚数（ニコチケ型）
   * @property {number}  ItemInfo.numOwned.kira     - 自排入手の所持枚数（キラチケ型）
   * @property {Object}  ItemInfo.numReleased       - null または { usual: number, nico: number, kira: number }
   * @property {number}  ItemInfo.numReleased.usual - 自排入手後に放出した枚数（ノーマルプリチケ型）
   * @property {number}  ItemInfo.numReleased.nico  - 自排入手後に放出した枚数（ニコチケ型）
   * @property {number}  ItemInfo.numReleased.kira  - 自排入手後に放出した枚数（キラチケ型）
   * @property {boolean} ItemInfo.isProcured        - 他排を買った・貰った場合に true
   * @property {boolean} ItemInfo.isRecruiting      - 「お譲りください」の場合に true
   * @property {boolean} ItemInfo.unnecessary       - 既所持の再録アイテムなど「取得不要」の場合に true
   */

  /**
   * アイテム情報をつくる
   * @param {ItemInfo} [payload]    アイテム情報に混ぜ込みたい内容
   * @return {ItemInfo} アイテム情報
   */
  export function item (payload) {
    const item = {
      collection  : '',
      id          : uuidv4(),
      rare        : '',
      name        : '',
      imgUrl      : '',
      kind        : '',
      color       : '',
      brand       : '',
      type        : '',
      point       : 0,
      aggregation : '',
      numOwned    : null,
      numReleased : null,
      isProcured  : false,
      isRecruiting: false,
      unnecessary : false,
    }
    return Object.assign(item, payload)
  }

  /**
   * アイテムリストの中から特定アイテムの情報を探す
   * @param {ItemInfo[]} items   - アイテムリスト
   * @param {string}     id      - 探す対象のアイテム id
   * @param {ItemInfo}   payload - アイテム情報に混ぜ込みたい内容
   * @return {ItemInfo} アイテム情報
   */
  export function externalItem (items = [], id = '', payload = {}) {
    const found = _.findWhere(items, { id }) || item({})
    const aggregation = found.aggregation || id
    return Object.assign(found, { aggregation }, payload)
  }

  export default {
    components: { ItemBadge },
    props: {
      period      : String,
      collection  : String,
      id          : String,
      rare        : String,
      name        : String,
      imgUrl      : String,
      kind        : String,
      bgcolor     : String,

      // TODO: 以下は Firebase から復元するときコメントアウト
      numOwned    : Object,
      numReleased : Object,
      isProcured  : Boolean,
      isRecruiting: Boolean,
      unnecessary : Boolean,
    },

    data () {
      return {
        // // TODO: 以下は Firebase から復元するときコメントアウトを外す
        // numOwned    : null,
        // numReleased : null,
        // isProcured  : false,
        // isRecruiting: false,
      }
    },

    computed: {
      storedItem () {
        return this.$store.state.user.items[ this.id ] || null
      },

      isPlaceholder () {
        return !this.name
      },

      isNotOwned () {
        const { isProcured, numOwned } = this
        return !isProcured && numOwned && !numOwned.kira && !numOwned.usual
      },
    },

    watch: {
      storedItem (storedItem) {
        const props = Object.keys(this.$options.data())
        props.forEach(prop => {
          const stored = storedItem[prop]
          if (!_.isUndefined(stored)) {
            this[prop] = stored
          }
        })
      },
    },

    created () {
    },

    mounted () {
    },

    beforeDestroy () {
    },

    methods: {
      updateAmount () {
        // this.$store.dispatch('user/updateStoredItems', items)
      },
    },

    filters: {
    },
  }
</script>


<template>
  <figure class="Item" :class="{ '-notOwned': isNotOwned }" :style="{ background: bgcolor }" :title="name" :hidden="isPlaceholder">
    <img v-if="imgUrl" v-lazy="imgUrl" class="Item__img" alt="" @click="updateAmount()">
    <i class="Item__throbber"></i>
    <span class="Item__status">
      <ItemBadge v-if="numOwned    && (numOwned.kira  || (numReleased && numReleased.kira ))" types="num owned kira " :value="numOwned.kira " />
      <ItemBadge v-if="numOwned    && (numOwned.nico  || (numReleased && numReleased.nico ))" types="num owned nico " :value="numOwned.nico " />
      <ItemBadge v-if="numOwned    && (numOwned.usual || (numReleased && numReleased.usual))" types="num owned usual" :value="numOwned.usual" />
      <ItemBadge v-if="numReleased && numReleased.kira " types="num released kira " :value="numReleased.kira " />
      <ItemBadge v-if="numReleased && numReleased.nico " types="num released nico " :value="numReleased.nico " />
      <ItemBadge v-if="numReleased && numReleased.usual" types="num released usual" :value="numReleased.usual" />
      <ItemBadge v-if="isProcured  " types="flag procured   " />
      <ItemBadge v-if="isRecruiting" types="flag recruiting " />
      <ItemBadge v-if="unnecessary " types="flag unnecessary" />
    </span>
    <figcaption class="Item__spec">
      <span class="Item__kind" hidden>{{ kind }}</span>
      <span class="Item__rare" hidden>{{ rare }}</span>
      <span class="Item__name">{{ name }}</span>
      <span class="Item__id"  >{{ id | placeholder('TBD') }}</span>
    </figcaption>
  </figure>
</template>


<style lang="scss" scoped>
  @import '~@/stylesheets/common';

  .Item {
    & {
      display: block;
      position: relative;
      z-index: auto;
      box-sizing: border-box;
      width: $item-width;
      height: auto;
      margin: 0 (-$item-padding) (-$item-padding) 0;
      padding: $item-padding $item-padding ($item-padding * 2);
      font-family: $font-family--default;
      box-shadow: 8px 8px 8px -4px rgba(black, 0.15);

      // アイテム画像の表示領域をあらかじめ白くしておく板。
      // img 要素を lazyload によりフェードインするためには必要。
      &::before {
        position: absolute;
        z-index: 0;
        display: block;
        width: $item-image-size;
        height: $item-image-size;
        background: white;
        content: "";
      }

      // 持っていないアイテムの場合
      &.-notOwned {
        &::after {
          display: block;
          position: absolute;
          z-index: 1;
          top: $item-padding;
          bottom: $item-padding;
          left: $item-padding;
          right: $item-padding;
          // background: rgba(black, 0.35);
          background: rgba(black, 0.00);
          content: "";
        }
      }

      // 非表示（プレースホルダ扱い）の場合
      &[hidden] {
        visibility: hidden;
      }
    }


    &__img {
      position: relative;
      z-index: 1;
      display: block;
      width: $item-image-size;
      height: $item-image-size;
      margin-bottom: $item-padding;
      background: white;
      transition: opacity 500ms ease-out;

      &,
      &[lazy="loading"],
      &[lazy="error"] {
        opacity: 0;
      }
      &[lazy="loaded"] {
        opacity: 1;
      }
    }

    &__img + &__throbber {
      display: flex;
      justify-content: center;
      align-items: center;
      position: absolute;
      z-index: 0;
      top: $item-padding;
      left: $item-padding;
      width: $item-image-size;
      height: $item-image-size;
      font-weight: bold;
      font-style: normal;
      font-size: $size-unit * 12;
      line-height: 1;
      background: transparent center no-repeat;
      opacity: 0.3;
    }
    &__img[lazy="loading"] + &__throbber {
      background-image: url(../assets/loading_01.gif);
    }
    &__img[lazy="error"] + &__throbber {
      &::before {
        color: #ff5d91;
        content: "×";
      }
    }

    &__status {
      display: flex;
      flex-direction: column;

      position: absolute;
      z-index: 2;
      top: 0;
      left: 0;
      height: $item-image-size + $item-padding;

      .ItemBadge {
        position: relative;
        top: -0.5 * $item-padding;
        left: -0.5 * $item-padding;

        &:not(:first-child) {
          margin-top: $size-unit * 1;
        }

        &.-flag.-recruiting,
        &.-flag.-unnecessary {
          position: absolute;
          top: auto;
          left: $item-padding * 1.25;
          bottom: $item-padding * 0.25;
        }
      }
    }

    &__kind,
    &__rare,
    &__name,
    &__id {
      overflow: hidden;
      text-overflow: ellipsis;
      display: block;
      font-size: 11px;
      line-height: 1.4;
      white-space: nowrap;
      text-align: left;

      &[hidden] {
        display: none;
      }
    }
  }
</style>
